/**
 * The Graphics area (0.92.0): the workspace's graphics assets, with search and a collection filter, and New, Edit
 * and Duplicate in PRISM Graphics. Edit and Duplicate are for graphics made in PRISM Graphics (OGraf packages with
 * editor metadata); all three need Upload media. Without PRISM_GRAPHICS_EDITOR_ORIGIN the area says PRISM Graphics
 * is not configured and offers no editing. Delete (0.95.0, with Delete assets) moves a graphic to the recycle bin.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Pencil, Plus, Search, Shapes, Trash2 } from 'lucide-react';
import { useCollections, useDeleteActions, useGraphics, useGraphicsSettings, useMe, useRecycleSettings, type GraphicListItem } from '../../api/hooks';
import { errorMessage } from '../../api/client';
import { toast } from '../../components/Toasts';
import { EmptyState } from '../../components/EmptyState';
import { useDebounced } from '../../lib/useDebounced';
import { relative } from '../../lib/format';
import { graphicsEditor } from '../../graphics/GraphicsEditor';

const ENGINE_LABEL: Record<string, string> = { 'ograf-v1': 'OGraf', renderat: 'HTML template' };

export const fps = (r: { num: number; den: number } | null) => (r ? `${Number((r.num / r.den).toFixed(3))} fps` : '—');

export function Graphics() {
  const me = useMe().data!;
  const settings = useGraphicsSettings();
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState<string>('');
  const q = useGraphics({ q: useDebounced(search, 250), collection: folder || null });
  const collections = useCollections();
  const configured = Boolean(settings.data?.editorOrigin);
  const canEdit = me.permissions.includes('asset.upload');
  const canDelete = me.permissions.includes('asset.delete');
  const items = q.data?.items ?? [];
  const folders = collections.data?.items ?? [];

  return (
    <>
      <header className="topbar">
        <h1><Shapes size={16} color="var(--accent-strong)" /> Graphics</h1>
        <div className="search" role="search">
          <Search size={15} />
          <input className="input" placeholder="Search graphics…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search graphics" data-testid="graphics-search" />
        </div>
        <select className="input" style={{ width: 220 }} value={folder} onChange={(e) => setFolder(e.target.value)} aria-label="Collection" data-testid="graphics-folder">
          <option value="">All collections</option>
          {folders.map((c) => <option key={c.id} value={c.id}>{c.name}{c.scope === 'public' ? ' (public)' : c.scope === 'shared' ? ' (shared)' : ''}</option>)}
        </select>
        <div className="topbar-spacer" />
        {configured && canEdit && (
          <button className="btn primary" data-testid="graphics-new"
            onClick={() => graphicsEditor.open({ mode: 'new', ...(folder ? { folderId: folder } : {}) })}><Plus size={14} /> New graphic</button>
        )}
      </header>
      <div className="page" data-testid="graphics-area">
        {settings.data && !configured && (
          <div className="panel-card" data-testid="graphics-not-configured" style={{ maxWidth: 760, marginBottom: 20 }}>
            <h3 style={{ marginTop: 0 }}>PRISM Graphics is not configured</h3>
            <p className="muted" style={{ margin: 0 }}>
              Graphics are listed and can be used as before, but they cannot be made or edited here. An administrator turns this on by setting the
              PRISM Graphics editor’s address in PRISM_GRAPHICS_EDITOR_ORIGIN for the API and the web tier.
            </p>
          </div>
        )}
        {configured && !canEdit && <p className="lede">Your role can look at graphics. Making and editing them needs the Upload media permission.</p>}
        {q.isLoading ? <div className="skeleton" style={{ height: 160 }} /> : items.length === 0 ? (
          <EmptyState style={{ minHeight: 220 }} icon={<Shapes size={24} />} title={search || folder ? 'No graphics match' : 'No graphics yet'}>
            <p>{search || folder ? 'Try another search or collection.' : configured && canEdit ? 'Make one with New graphic.' : 'Graphics packages uploaded to the library appear here.'}</p>
          </EmptyState>
        ) : (
          <table className="table" aria-label="Graphics" data-testid="graphics-table">
            <thead><tr>
              <th style={{ width: 88 }} aria-label="Preview" /><th>Name</th><th>Resolution</th><th>Frame rate</th><th>Steps</th><th>Fields</th><th>Engine</th><th>Version</th>
              <th aria-label="Actions" />
            </tr></thead>
            <tbody>
              {items.map((g) => <GraphicRow key={g.assetId} g={g} editing={configured && canEdit} deleting={canDelete} />)}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function GraphicRow({ g, editing, deleting }: { g: GraphicListItem; editing: boolean; deleting: boolean }) {
  const why = g.editable ? undefined : 'Only graphics made in PRISM Graphics can be edited there';
  const { remove, restore } = useDeleteActions();
  const bin = useRecycleSettings(deleting).data?.enabled ?? true;
  const del = () => {
    const n = g.usedInSequences;
    const used = n ? `\n\nUsed in ${n === 1 ? 'a sequence' : `${n} sequences`}; those clips keep rendering the version they use.` : '';
    if (!window.confirm(`${bin ? 'Move' : 'Delete'} “${g.name}”${bin ? ' to the recycle bin' : ''}? It is no longer offered for new clips.${used}`)) return;
    // The usage was in the question, so the API's in-use check is confirmed too.
    remove.mutate({ id: g.assetId, confirmInUse: true }, {
      onSuccess: (res) => toast(res.recycleBin ? `Moved “${g.name}” to the recycle bin` : `Deleted “${g.name}”`, 'info', {
        label: 'Undo', onClick: () => restore.mutate(g.assetId, { onSuccess: () => toast(`Restored “${g.name}”`), onError: (e) => toast(errorMessage(e), 'error') }),
      }),
      onError: (e) => toast(errorMessage(e), 'error'),
    });
  };
  return (
    <tr data-testid="graphic-row" data-asset-id={g.assetId}>
      <td>{g.thumbnailUrl ? <img className="mini-thumb" src={g.thumbnailUrl} alt="" loading="lazy" /> : <div className="mini-thumb" />}</td>
      <td className="wrap" style={{ fontWeight: 550, minWidth: 160 }}>
        <Link to={`/assets/${g.assetId}`}>{g.name}</Link>
        <div className="faint" style={{ fontSize: 12 }}>
          {g.status === 'failed' ? <span title={g.error?.message} style={{ color: 'var(--muted)' }}>Could not be processed</span>
            : g.status === 'processing' ? 'Processing…' : `Updated ${relative(g.updatedAt)}`}
        </div>
      </td>
      <td className="mono">{g.resolution ? `${g.resolution.width}×${g.resolution.height}` : '—'}</td>
      <td className="mono">{fps(g.frameRate)}</td>
      <td className="mono">{g.stepCount ?? '—'}</td>
      <td className="mono">{g.fieldCount ?? '—'}</td>
      <td>{g.engine ? ENGINE_LABEL[g.engine] ?? g.engine : '—'}</td>
      <td className="mono" data-testid="graphic-version">v{g.currentVersion}</td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {editing && (
          <>
            <button className="btn sm" disabled={!g.editable} title={why} data-testid="graphic-edit"
              onClick={() => graphicsEditor.open({ mode: 'edit', assetId: g.assetId, title: g.name })}><Pencil size={13} /> Edit</button>{' '}
            <button className="btn ghost sm" disabled={!g.editable} title={why ?? 'A new graphic, starting from this one'} data-testid="graphic-duplicate"
              onClick={() => graphicsEditor.open({ mode: 'new', assetId: g.assetId, duplicate: true, title: g.name })}><Copy size={13} /> Duplicate</button>
          </>
        )}
        {deleting && (
          <>{' '}<button className="btn ghost sm danger" disabled={remove.isPending} data-testid="graphic-delete" title="Move to the recycle bin" onClick={del}><Trash2 size={13} /> Delete</button></>
        )}
      </td>
    </tr>
  );
}
